/**
 * PackagePreviewPanel — compact read-only summary of a package.
 *
 * Used inside the hover-card and the draggable Quick View modal. Loads
 * the package on mount from `/packages/{pid}` and renders header +
 * key totals + sectioned line list, mirroring the shape of the
 * Package editor's Overview so the preview feels familiar.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, ExternalLink, Package as PackageIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

const fmtMoney = (n, cur = "INR") => {
  const v = Number(n) || 0;
  if (!v) return "—";
  try {
    return v.toLocaleString(undefined, { style: "currency", currency: cur, maximumFractionDigits: 0 });
  } catch {
    return v.toLocaleString();
  }
};

export default function PackagePreviewPanel({ pid, compact = false, tight = false, onNavigate }) {
  const [pkg, setPkg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!pid) return;
    let alive = true;
    setPkg(null); setErr(null);
    api.get(`/packages/${pid}`).then((r) => {
      if (!alive) return;
      setPkg(r.data);
    }).catch((e) => {
      if (!alive) return;
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    });
    return () => { alive = false; };
  }, [pid]);

  if (err) {
    return <div className="p-4 text-[11px] text-destructive" data-testid="pkg-preview-error">Could not load package · {err}</div>;
  }
  if (!pkg) {
    return (
      <div className="p-6 flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="pkg-preview-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
      </div>
    );
  }

  const t = pkg.totals || {};
  const sections = pkg.sections || [];
  const lines = pkg.lines || [];
  // group lines by section_id, preserving order.
  const linesBySection = new Map();
  for (const s of sections) linesBySection.set(s.id, []);
  linesBySection.set("_ungrouped", []);
  for (const ln of lines) {
    const key = ln.section_id && linesBySection.has(ln.section_id) ? ln.section_id : "_ungrouped";
    linesBySection.get(key).push(ln);
  }

  return (
    <div className="flex flex-col h-full" data-testid={`pkg-preview-${pid}`}>
      {/* Header */}
      <div className={`${tight ? "px-2.5 py-1.5" : "px-4 py-3"} border-b border-border bg-muted/20 shrink-0`}>
        <div className="flex items-start gap-2">
          <PackageIcon className={`${tight ? "w-3 h-3 mt-0.5" : "w-4 h-4 mt-0.5"} text-primary shrink-0`} />
          <div className="min-w-0 flex-1">
            <div className={`${tight ? "text-[11px] leading-tight" : "text-sm"} font-semibold truncate`} data-testid="pkg-preview-name">{pkg.name}</div>
            <div className={`${tight ? "text-[9.5px]" : "text-[10.5px]"} text-muted-foreground truncate`}>
              {[pkg.client_name, pkg.brand_name].filter(Boolean).join(" · ") || "—"}
            </div>
            <div className="flex items-center gap-1 flex-wrap mt-0.5">
              <Badge variant="outline" className={`rounded-none ${tight ? "text-[8.5px]" : "text-[9px]"} px-1 py-0`}>{pkg.status}</Badge>
              {pkg.start_date && (
                <span className={`${tight ? "text-[8.5px]" : "text-[9px]"} font-mono text-muted-foreground`}>
                  {pkg.start_date} → {pkg.end_date || "∞"}
                </span>
              )}
              {pkg.project_name && (
                <span className={`${tight ? "text-[8.5px]" : "text-[9px]"} chip bg-primary/10 text-primary border-primary/30 px-1 py-0`}>
                  {pkg.project_name}
                </span>
              )}
            </div>
          </div>
          <Link to={`/app/packages/${pid}`} target="_blank" rel="noreferrer"
                onClick={onNavigate}
                className={`${tight ? "text-[9px]" : "text-[10px]"} text-primary hover:underline inline-flex items-center gap-0.5 shrink-0`}
                data-testid="pkg-preview-open">
            Open <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-0 border-b border-border shrink-0">
        {t.net_monthly_price !== undefined && (
          <StatCell tight={tight} label="Monthly" value={fmtMoney(t.net_monthly_price, pkg.currency)} testid="pkg-preview-stat-monthly" />
        )}
        <StatCell tight={tight} label="Hours / mo" value={`${t.monthly_hours ?? "—"}h`} testid="pkg-preview-stat-hours" />
        <StatCell tight={tight} label="Lines" value={`${lines.length || 0}${sections.length ? ` · ${sections.length} sec` : ""}`} testid="pkg-preview-stat-lines" />
      </div>

      {/* Lines list */}
      <div className={`flex-1 min-h-0 overflow-auto ${compact ? "" : "px-1"}`} data-testid="pkg-preview-lines">
        {lines.length === 0 && (
          <div className={`${tight ? "p-2 text-[10px]" : "p-4 text-[11px]"} text-muted-foreground italic text-center`}>No lines in this package.</div>
        )}
        {/* R268bbbb · Feb 2026 — Sticky top column headers so PMs
            know exactly which value is Frequency vs Quantity vs
            Hours. Uses the same 12-col grid as every item row so
            every field lines up regardless of how many groups are
            in the package. */}
        {lines.length > 0 && (
          <div className={`${tight ? "px-2 py-0.5 text-[8.5px]" : "px-2.5 py-1 text-[9px]"} grid grid-cols-12 gap-1 uppercase tracking-widest text-muted-foreground bg-muted/50 border-b border-border sticky top-0 z-10`}
               data-testid="pkg-preview-line-header">
            <div className="col-span-6">Item</div>
            <div className="col-span-2">Freq</div>
            <div className="col-span-2 text-right">Qty</div>
            <div className="col-span-2 text-right">Hrs</div>
          </div>
        )}
        {[...linesBySection.entries()].map(([secId, ls]) => {
          if (ls.length === 0) return null;
          const sec = sections.find((s) => s.id === secId);
          // R160e · Show item count in each section header so the preview
          // gives a quick sense of the package's shape at a glance.
          // R268bbbb · Feb 2026 — Section header is now laid out on
          // the SAME 12-col grid as the item rows, so its title and
          // count align with the Item / Hrs columns below and every
          // section reads as one continuous table.
          return (
            <div key={secId} className="border-b border-border/60 last:border-b-0">
              <div className={`${tight ? "px-2 py-0.5 text-[8.5px]" : "px-2.5 py-1 text-[9px]"} grid grid-cols-12 gap-1 items-center uppercase tracking-widest font-semibold text-muted-foreground bg-muted/30`}>
                <span className="col-span-8 truncate">{sec?.name || (secId === "_ungrouped" ? "Ungrouped" : "Section")}</span>
                <span className={`col-span-4 text-right font-mono normal-case tracking-normal ${tight ? "text-[8.5px]" : "text-[9px]"} text-muted-foreground/70`} data-testid={`pkg-preview-section-count-${secId}`}>
                  {ls.length} {ls.length === 1 ? "item" : "items"}
                </span>
              </div>
              <ul>
                {ls.map((ln) => (
                  <li key={ln.id} className={`${tight ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]"} grid grid-cols-12 gap-1 items-center border-t border-border/40 leading-tight`}>
                    <div className="col-span-6 truncate">
                      <span className="font-medium">{ln.name}</span>
                      {ln.category && (
                        <span className={`chip ${tight ? "text-[8px]" : "text-[8.5px]"} bg-muted/60 text-muted-foreground border-border ml-1 px-1 py-0`}>{ln.category}</span>
                      )}
                    </div>
                    <div className={`col-span-2 ${tight ? "text-[9px]" : "text-[10px]"} font-mono text-muted-foreground truncate`}>{ln.frequency || "—"}</div>
                    <div className={`col-span-2 text-right ${tight ? "text-[9px]" : "text-[10px]"} font-mono`} title={ln.frequency === "monthly" ? "Per month" : "Total quantity"}>
                      {(ln.qty_per_month ?? ln.quantity ?? ln.count ?? "—")}×
                      {ln.frequency === "monthly" && (ln.qty_per_month ?? ln.quantity ?? ln.count) != null && (
                        <span className={`ml-0.5 ${tight ? "text-[7.5px]" : "text-[8.5px]"} text-muted-foreground/70`}>/mo</span>
                      )}
                    </div>
                    <div className={`col-span-2 text-right ${tight ? "text-[9px]" : "text-[10px]"} font-mono`}>
                      {ln.effort_hours ? `${ln.effort_hours}h` : "—"}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCell({ label, value, testid, tight }) {
  return (
    <div className={`${tight ? "px-2 py-1" : "px-2.5 py-1.5"} border-r border-border last:border-r-0`} data-testid={testid}>
      <div className={`${tight ? "text-[8px]" : "text-[9px]"} uppercase tracking-widest text-muted-foreground`}>{label}</div>
      <div className={`${tight ? "text-[10px]" : "text-xs"} font-mono font-semibold`}>{value}</div>
    </div>
  );
}
